import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Badge,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCanAccess, useDataProvider } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '@/providers/loginWithEmail/dataProvider';
import { formatRelativeBackendDate } from '@/utils/date_utils';
import LeadReminderActions from './LeadReminderActions';
import {
    clearPushPermissionPromptDismiss,
    getBrowserNotificationPermission,
    isBrowserNotificationSupported,
    needsBrowserNotificationPermission,
    showBrowserNotification,
    wasPushPermissionPromptDismissed,
    type BrowserPermission,
} from './browserNotifications';
import {
    AppNotification,
    subscribeNotificationStream,
} from './notificationApi';
import PushPermissionOverlay from './PushPermissionOverlay';

const LIST_LIMIT = 8;

export const notificationPath = (
    item: AppNotification,
    canAccessTickets = true
) => {
    // Ticket authors without ticket:read land on the help panel's My tickets tab.
    if (item.ticket_id != null && !canAccessTickets) {
        return '?help=tickets';
    }
    if (item.link) return item.link;
    if (item.ticket_id) return `/tickets/${item.ticket_id}/show`;
    if (item.quote_id) return `/quotes/${item.quote_id}/show`;
    return '/';
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const { canAccess: canAccessTickets = false } = useCanAccess({
        resource: 'tickets',
        action: 'show',
    });
    const canAccessTicketsRef = useRef(canAccessTickets);
    canAccessTicketsRef.current = canAccessTickets;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [items, setItems] = useState<AppNotification[]>([]);
    const [unread, setUnread] = useState(0);
    const [toastQueue, setToastQueue] = useState<AppNotification[]>([]);
    const [permission, setPermission] = useState<BrowserPermission>(() =>
        getBrowserNotificationPermission()
    );
    const [showPermissionOverlay, setShowPermissionOverlay] = useState(false);
    const [holdMenu, setHoldMenu] = useState(false);
    const knownIdsRef = useRef(new Set<number>());
    const toast = toastQueue[0] ?? null;

    const refresh = useCallback(async () => {
        const token = getAccessToken();
        if (!token) return;
        try {
            const [list, count] = await Promise.all([
                dataProvider.getNotifications() as Promise<AppNotification[]>,
                dataProvider.getUnreadNotificationCount() as Promise<number>,
            ]);
            knownIdsRef.current = new Set(list.map(n => n.id));
            setItems(list);
            setUnread(count);
        } catch {
            // Keep last known state if the request fails.
        }
    }, [dataProvider]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Prompt once per session when the browser has not decided yet.
    useEffect(() => {
        if (!isBrowserNotificationSupported()) return;
        if (!needsBrowserNotificationPermission()) return;
        if (wasPushPermissionPromptDismissed()) return;
        setShowPermissionOverlay(true);
    }, []);

    useEffect(() => {
        const token = getAccessToken();
        if (!token) return;

        const unsubscribe = subscribeNotificationStream(token, {
            onMessage: payload => {
                if (knownIdsRef.current.has(payload.id)) return;
                knownIdsRef.current.add(payload.id);
                setItems(prev => [payload, ...prev].slice(0, LIST_LIMIT));
                setUnread(prev => prev + (payload.read_at ? 0 : 1));
                setToastQueue(prev => [...prev, payload]);
                showBrowserNotification({
                    body: payload.message,
                    tag: `notification-${payload.id}`,
                    onClick: () => {
                        navigate(
                            notificationPath(payload, canAccessTicketsRef.current)
                        );
                    },
                });
            },
            onReconnect: refresh,
        });
        return unsubscribe;
    }, [navigate, refresh]);

    const open = Boolean(anchorEl);
    const pushNeedsAttention =
        permission === 'default' || permission === 'denied';

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        refresh();
    };

    const handlePermissionChange = (next: BrowserPermission) => {
        setPermission(next);
        if (next === 'granted') {
            clearPushPermissionPromptDismiss();
            setShowPermissionOverlay(false);
        }
    };

    const openPermissionOverlay = () => {
        handleClose();
        clearPushPermissionPromptDismiss();
        setShowPermissionOverlay(true);
    };

    const handleClose = () => {
        if (holdMenu) return;
        setAnchorEl(null);
    };

    const dismissToast = () => {
        setToastQueue(prev => prev.slice(1));
    };

    const openNotification = async (item: AppNotification) => {
        const token = getAccessToken();
        if (token && !item.read_at) {
            try {
                const updated = await dataProvider.markNotificationRead(item.id);
                setItems(prev =>
                    prev.map(n => (n.id === item.id ? updated : n))
                );
                setUnread(prev => Math.max(0, prev - 1));
            } catch {
                // Navigate anyway.
            }
        }
        navigate(notificationPath(item, canAccessTickets));
    };

    const handleClickItem = async (item: AppNotification) => {
        handleClose();
        await openNotification(item);
    };

    const handleToastClick = async () => {
        if (!toast) return;
        const item = toast;
        dismissToast();
        await openNotification(item);
    };

    const handleRemove = async (
        event: React.MouseEvent,
        item: AppNotification
    ) => {
        event.stopPropagation();
        const token = getAccessToken();
        if (!token) return;
        try {
            await dataProvider.softDeleteNotification(item.id);
            knownIdsRef.current.delete(item.id);
            setItems(prev => prev.filter(n => n.id !== item.id));
            if (!item.read_at) {
                setUnread(prev => Math.max(0, prev - 1));
            }
            setToastQueue(prev => prev.filter(n => n.id !== item.id));
        } catch {
            // Keep item if delete failed.
        }
    };

    const handleMarkAll = async () => {
        const token = getAccessToken();
        if (!token) return;
        try {
            await dataProvider.markAllNotificationsRead();
            setItems(prev =>
                prev.map(n => ({
                    ...n,
                    read_at: n.read_at ?? new Date().toISOString(),
                }))
            );
            setUnread(0);
        } catch {
            // Ignore.
        }
    };

    const handleReminderResolved = (id: number) => {
        knownIdsRef.current.delete(id);
        setItems(prev => prev.filter(n => n.id !== id));
        setToastQueue(prev => prev.filter(n => n.id !== id));
        void refresh();
    };

    return (
        <>
            <Tooltip
                title={
                    pushNeedsAttention
                        ? 'Enable browser notifications for follow-ups'
                        : 'Notifications'
                }
            >
                <IconButton
                    color="inherit"
                    aria-label="Notifications"
                    onClick={handleOpen}
                    size="small"
                >
                    <Badge badgeContent={unread} color="error" max={99}>
                        {permission === 'denied' ? (
                            <NotificationsOffIcon fontSize="small" />
                        ) : (
                            <NotificationsIcon fontSize="small" />
                        )}
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: { sx: { width: 380, maxHeight: 440 } },
                }}
            >
                <Box
                    px={2}
                    py={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="subtitle2">Notifications</Typography>
                    {unread > 0 && (
                        <Typography
                            variant="caption"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleMarkAll}
                        >
                            Mark all read
                        </Typography>
                    )}
                </Box>
                {isBrowserNotificationSupported() && pushNeedsAttention && (
                    <>
                        <Divider />
                        <Box px={2} py={1.5}>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                {permission === 'denied'
                                    ? 'Browser notifications are blocked. Allow them in site settings to get follow-up alerts.'
                                    : 'Allow browser notifications to get follow-up alerts when estimates need attention.'}
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={openPermissionOverlay}
                            >
                                {permission === 'denied'
                                    ? 'How to enable'
                                    : 'Enable notifications'}
                            </Button>
                        </Box>
                    </>
                )}
                <Divider />
                {items.length === 0 ? (
                    <Box px={2} py={2}>
                        <Typography variant="body2" color="text.secondary">
                            No notifications
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding>
                        {items.map(item => {
                            const isLeadReminder = item.kind === 'lead_next_step';
                            return (
                            <ListItem
                                key={item.id}
                                disablePadding
                                secondaryAction={
                                    isLeadReminder ? (
                                        <LeadReminderActions
                                            item={item}
                                            token={getAccessToken()}
                                            onResolved={handleReminderResolved}
                                            onDialogOpenChange={setHoldMenu}
                                        />
                                    ) : (
                                        <IconButton
                                            edge="end"
                                            aria-label="Remove notification"
                                            size="small"
                                            onClick={e => handleRemove(e, item)}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )
                                }
                                sx={{
                                    bgcolor: item.read_at
                                        ? undefined
                                        : 'action.hover',
                                    '&:hover .view-item-chip': {
                                        opacity: 1,
                                    },
                                }}
                            >
                                <ListItemButton
                                    onClick={() => handleClickItem(item)}
                                    aria-label="Open notification"
                                    sx={{
                                        pr: isLeadReminder ? 16 : 6,
                                        alignItems: 'flex-start',
                                        position: 'relative',
                                    }}
                                >
                                    <ListItemText
                                        primary={item.message}
                                        secondary={
                                            item.created_at
                                                ? formatRelativeBackendDate(
                                                      item.created_at
                                                  )
                                                : undefined
                                        }
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: item.read_at
                                                ? undefined
                                                : 600,
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                        }}
                                    />
                                    {isLeadReminder ? null : (
                                    <Chip
                                        className="view-item-chip"
                                        size="small"
                                        icon={<OpenInNewIcon />}
                                        label="View"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 10,
                                            right: 80,
                                            height: 22,
                                            fontSize: 11,
                                            pointerEvents: 'none',
                                            bgcolor: 'background.paper',
                                            opacity: 0,
                                            transition: 'opacity 0.15s ease',
                                        }}
                                    />
                                    )}
                                </ListItemButton>
                            </ListItem>
                            );
                        })}
                    </List>
                )}
            </Menu>
            <Snackbar
                open={!!toast}
                autoHideDuration={8000}
                onClose={(_, reason) => {
                    if (reason === 'clickaway') return;
                    dismissToast();
                }}
                message={toast?.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                ContentProps={{
                    'aria-live': 'polite',
                    role: 'status',
                }}
                action={
                    <Button color="secondary" size="small" onClick={handleToastClick}>
                        View
                    </Button>
                }
            />
            <PushPermissionOverlay
                open={showPermissionOverlay}
                permission={permission}
                onClose={() => setShowPermissionOverlay(false)}
                onPermissionChange={handlePermissionChange}
            />
        </>
    );
};

export default NotificationBell;
